import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, GripVertical, ChevronUp, ChevronDown, X, Link, MousePointerClick, Shuffle, AlertCircle, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { updateNode } from '@/services/api';
import { getPageVariables } from '@/services/api/connections';
import { useQuery } from '@tanstack/react-query';
import type { ContentBlock, BlockButton, UrlEntry } from '@/types/flow';
import { blockTypes } from '../constants/nodeCategories';
import type { BlockType } from '../constants/nodeCategories';
import MediaLibraryModal from './MediaLibraryModal';
import SmartTextField from '@/pages/Meta/Broadcast/components/SmartTextField';

interface BlocksEditorProps {
    nodeId: number;
    initialBlocks: ContentBlock[];
    pageId: string;
}

export default function BlocksEditor({ nodeId, initialBlocks, pageId }: BlocksEditorProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [blocks, setBlocks] = useState<ContentBlock[]>(initialBlocks);
    const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
    const [mediaModalIndex, setMediaModalIndex] = useState<number | null>(null);
    const [mediaModalType, setMediaModalType] = useState<'image' | 'video' | 'audio'>('image');
    const [hasChanges, setHasChanges] = useState(false);

    // Carrega variáveis da página
    const { data: pageVariables = [] } = useQuery({
        queryKey: ['pageVariables', pageId],
        queryFn: () => getPageVariables(Number(pageId)),
        enabled: !!pageId,
        staleTime: 1000 * 60 * 5,
    });

    const saveMutation = useMutation({
        mutationFn: () => updateNode(nodeId, { config: { blocks } }),
        onSuccess: () => {
            toast({ title: 'Blocos salvos!', description: 'Os blocos foram atualizados.' });
            queryClient.invalidateQueries({ queryKey: ['funnel-details'] });
            setHasChanges(false);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar blocos',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    const addBlock = (type: BlockType) => {
        const newBlock: ContentBlock = { type };
        if (type === 'text') {
            newBlock.message = '';
        }
        setBlocks((prev) => [...prev, newBlock]);
        setIsAddMenuOpen(false);
        setHasChanges(true);
    };

    const removeBlock = (index: number) => {
        setBlocks((prev) => prev.filter((_, i) => i !== index));
        setHasChanges(true);
    };

    const updateBlock = (index: number, updates: Partial<ContentBlock>) => {
        setBlocks((prev) =>
            prev.map((block, i) => (i === index ? { ...block, ...updates } : block))
        );
        setHasChanges(true);
    };

    const moveBlock = (index: number, direction: 'up' | 'down') => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= blocks.length) return;
        const newBlocks = [...blocks];
        [newBlocks[index], newBlocks[newIndex]] = [newBlocks[newIndex], newBlocks[index]];
        setBlocks(newBlocks);
        setHasChanges(true);
    };

    // Button management
    const addButton = (blockIndex: number, type: 'url' | 'postback') => {
        const block = blocks[blockIndex];
        const currentButtons = block.buttons || [];
        if (currentButtons.length >= 3) return;
        const newButton: BlockButton = {
            text: '',
            type,
            value: '',
            url_entries: type === 'url' ? [{ url: '', weight: 100 }] : undefined,
        };
        updateBlock(blockIndex, { buttons: [...currentButtons, newButton] });
    };

    const updateButton = (blockIndex: number, btnIndex: number, updates: Partial<BlockButton>) => {
        const block = blocks[blockIndex];
        const newButtons = (block.buttons || []).map((btn, i) =>
            i === btnIndex ? { ...btn, ...updates } : btn
        );
        updateBlock(blockIndex, { buttons: newButtons });
    };

    const removeButton = (blockIndex: number, btnIndex: number) => {
        const block = blocks[blockIndex];
        const newButtons = (block.buttons || []).filter((_, i) => i !== btnIndex);
        updateBlock(blockIndex, { buttons: newButtons });
    };

    // URL Entries management
    const addUrlEntry = (blockIndex: number, btnIndex: number) => {
        const block = blocks[blockIndex];
        const btn = block.buttons?.[btnIndex];
        if (!btn || btn.type !== 'url') return;
        const currentEntries = btn.url_entries || [{ url: btn.value || '', weight: 100 }];
        const newEntries: UrlEntry[] = [...currentEntries, { url: '', weight: 0 }];
        updateButton(blockIndex, btnIndex, { url_entries: newEntries });
    };

    const updateUrlEntry = (blockIndex: number, btnIndex: number, entryIndex: number, updates: Partial<UrlEntry>) => {
        const block = blocks[blockIndex];
        const btn = block.buttons?.[btnIndex];
        if (!btn || btn.type !== 'url') return;
        const newEntries = (btn.url_entries || []).map((e, i) =>
            i === entryIndex ? { ...e, ...updates } : e
        );
        updateButton(blockIndex, btnIndex, { url_entries: newEntries });
    };

    const removeUrlEntry = (blockIndex: number, btnIndex: number, entryIndex: number) => {
        const block = blocks[blockIndex];
        const btn = block.buttons?.[btnIndex];
        if (!btn || btn.type !== 'url') return;
        const newEntries = (btn.url_entries || []).filter((_, i) => i !== entryIndex);
        updateButton(blockIndex, btnIndex, { url_entries: newEntries });
    };

    const openMediaModal = (index: number, type: 'image' | 'video' | 'audio') => {
        setMediaModalIndex(index);
        setMediaModalType(type);
    };

    const handleMediaSelect = (media: any) => {
        if (mediaModalIndex !== null) {
            updateBlock(mediaModalIndex, {
                media_url: media.file_url,
                media_filename: media.filename,
            });
            setMediaModalIndex(null);
        }
    };

    const getBlockTypeInfo = (type: string) => {
        return blockTypes.find((bt) => bt.type === type);
    };

    if (blocks.length === 0 && !isAddMenuOpen) {
        return (
            <div className="px-6 py-4 border-t border-gray-100">
                <button
                    onClick={() => setIsAddMenuOpen(true)}
                    className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors flex items-center justify-center gap-2 text-gray-500 hover:text-purple-600"
                >
                    <Plus className="w-4 h-4" />
                    <span className="text-sm font-medium">Adicionar Bloco</span>
                </button>

                {isAddMenuOpen && (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                        {blockTypes.map((bt) => {
                            const Icon = bt.icon;
                            return (
                                <button
                                    key={bt.type}
                                    onClick={() => addBlock(bt.type)}
                                    className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 hover:border-purple-400 hover:bg-purple-50 transition-colors"
                                >
                                    <div className={`${bt.color} p-1.5 rounded`}>
                                        <Icon className="w-3.5 h-3.5 text-white" />
                                    </div>
                                    <span className="text-sm text-gray-700">{bt.label}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="border-t border-gray-100">
            {/* Blocks list */}
            <div className="px-6 pt-4 pb-2 space-y-3">
                <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Blocos Adicionais ({blocks.length})
                    </span>
                </div>

                {blocks.map((block, index) => {
                    const info = getBlockTypeInfo(block.type);
                    const Icon = info?.icon;
                    const blockButtons = block.buttons || [];

                    return (
                        <div
                            key={index}
                            className="border border-gray-200 rounded-lg bg-gray-50 overflow-hidden"
                        >
                            {/* Block header */}
                            <div className="flex items-center gap-2 px-3 py-2 bg-white border-b border-gray-100">
                                <GripVertical className="w-3.5 h-3.5 text-gray-300" />
                                <div className={`${info?.color || 'bg-gray-500'} p-1 rounded`}>
                                    {Icon && <Icon className="w-3 h-3 text-white" />}
                                </div>
                                <span className="text-xs font-medium text-gray-600 flex-1">
                                    {info?.label || block.type}
                                </span>
                                <div className="flex items-center gap-0.5">
                                    <button
                                        onClick={() => moveBlock(index, 'up')}
                                        disabled={index === 0}
                                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                    >
                                        <ChevronUp className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                        onClick={() => moveBlock(index, 'down')}
                                        disabled={index === blocks.length - 1}
                                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                    >
                                        <ChevronDown className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                        onClick={() => removeBlock(index)}
                                        className="p-1 text-gray-400 hover:text-red-500"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>

                            {/* Block content */}
                            <div className="px-3 py-2">
                                {block.type === 'text' && (
                                    <SmartTextField
                                        value={block.message || ''}
                                        onChange={msg => updateBlock(index, { message: msg })}
                                        variables={pageVariables}
                                        placeholder="Digite o texto da mensagem..."
                                        rows={3}
                                    />
                                )}

                                {(block.type === 'image' ||
                                    block.type === 'video' ||
                                    block.type === 'audio') && (
                                        <div className="space-y-2">
                                            {block.media_url ? (
                                                <div className="flex items-center gap-2">
                                                    {block.type === 'image' && (
                                                        <img
                                                            src={block.media_url}
                                                            alt="Preview"
                                                            className="w-16 h-16 object-cover rounded border"
                                                        />
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs text-gray-600 truncate">
                                                            {block.media_filename || 'Arquivo selecionado'}
                                                        </p>
                                                        <button
                                                            onClick={() =>
                                                                openMediaModal(index, block.type as 'image' | 'video' | 'audio')
                                                            }
                                                            className="text-xs text-purple-600 hover:text-purple-700 mt-1"
                                                        >
                                                            Trocar
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() =>
                                                        openMediaModal(index, block.type as 'image' | 'video' | 'audio')
                                                    }
                                                    className="w-full py-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors flex flex-col items-center gap-1"
                                                >
                                                    {Icon && <Icon className="w-6 h-6 text-gray-400" />}
                                                    <span className="text-xs text-gray-500">
                                                        Selecionar {info?.label?.toLowerCase()}
                                                    </span>
                                                </button>
                                            )}
                                            {block.type === 'image' && (
                                                <Input
                                                    placeholder="URL ao clicar na imagem (opcional)"
                                                    value={block.default_action_url || ''}
                                                    onChange={(e) =>
                                                        updateBlock(index, { default_action_url: e.target.value })
                                                    }
                                                    className="h-7 text-xs"
                                                />
                                            )}
                                        </div>
                                    )}
                            </div>

                            {/* Block buttons */}
                            {blockButtons.length > 0 && (
                                <div className="px-3 pb-2 space-y-3">
                                    {blockButtons.map((btn, btnIdx) => {
                                        const isUrlBtn = btn.type === 'url';
                                        const entries = isUrlBtn ? (btn.url_entries?.length ? btn.url_entries : [{ url: btn.value || '', weight: 100 }]) : [];

                                        return (
                                            <div key={btnIdx} className="bg-white rounded-lg border border-gray-200 p-2.5 shadow-sm">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="flex-shrink-0">
                                                        {isUrlBtn ? (
                                                            <Link className="w-4 h-4 text-blue-500" />
                                                        ) : (
                                                            <MousePointerClick className="w-4 h-4 text-green-500" />
                                                        )}
                                                    </div>
                                                    <Input
                                                        placeholder="Texto do botão"
                                                        value={btn.text}
                                                        onChange={(e) => updateButton(index, btnIdx, { text: e.target.value })}
                                                        className="h-8 text-sm flex-1 font-medium"
                                                    />
                                                    <div className="w-24 flex-shrink-0">
                                                        <select
                                                            value={btn.type}
                                                            onChange={(e) => {
                                                                const val = e.target.value as 'url' | 'postback';
                                                                updateButton(index, btnIdx, {
                                                                    type: val,
                                                                    url_entries: val === 'url' ? (btn.url_entries?.length ? btn.url_entries : [{ url: btn.value || '', weight: 100 }]) : undefined
                                                                });
                                                            }}
                                                            className="w-full h-8 text-[11px] bg-gray-50 border border-gray-200 rounded-md px-1 focus:outline-none focus:ring-1 focus:ring-purple-400"
                                                        >
                                                            <option value="postback">Ação</option>
                                                            <option value="url">Link</option>
                                                        </select>
                                                    </div>
                                                    <button
                                                        onClick={() => removeButton(index, btnIdx)}
                                                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors flex-shrink-0"
                                                        title="Remover botão"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>

                                                {/* Action configuration */}
                                                <div className="pl-6 space-y-2">
                                                    {!isUrlBtn ? (
                                                        <Input
                                                            placeholder="Ação/Payload"
                                                            value={btn.value}
                                                            onChange={(e) => updateButton(index, btnIdx, { value: e.target.value })}
                                                            className="h-8 text-xs font-mono"
                                                        />
                                                    ) : (() => {
                                                        const totalWeight = entries.reduce((acc, curr) => acc + (curr.weight || 0), 0);
                                                        const weightOk = Math.round(totalWeight) === 100;

                                                        return (
                                                            <div className="space-y-2">
                                                                <div className="flex items-center justify-between">
                                                                    <div className="flex items-center gap-1.5">
                                                                        <Shuffle className="w-3.5 h-3.5 text-blue-500" />
                                                                        <span className="text-xs font-semibold text-gray-600">URLs com peso (random)</span>
                                                                    </div>
                                                                    {entries.length > 0 && (
                                                                        <span className={`text-xs font-mono font-semibold px-2 py-0.5 rounded-full ${weightOk ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                                                                            {totalWeight}%
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                {entries.map((entry, entryIdx) => (
                                                                    <div key={entryIdx} className="bg-blue-50 border border-blue-100 rounded-lg p-2.5 space-y-1.5">
                                                                        <div className="flex items-center justify-between mb-1.5">
                                                                            <span className="text-xs font-semibold text-blue-700">URL {entryIdx + 1}</span>
                                                                            {entries.length > 1 && (
                                                                                <button
                                                                                    onClick={() => removeUrlEntry(index, btnIdx, entryIdx)}
                                                                                    className="p-0.5 text-blue-400 hover:text-red-500 transition-colors"
                                                                                >
                                                                                    <X className="w-3 h-3" />
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                        <SmartTextField
                                                                            value={entry.url}
                                                                            onChange={url => updateUrlEntry(index, btnIdx, entryIdx, { url })}
                                                                            variables={pageVariables}
                                                                            placeholder="https://exemplo.com/{{variavel}}"
                                                                            singleLine
                                                                            inputClassName="h-7 text-xs bg-white"
                                                                        />
                                                                        <div className="flex items-center gap-2 mt-1">
                                                                            <span className="text-xs text-blue-600 whitespace-nowrap">Peso (%)</span>
                                                                            <Input
                                                                                type="number"
                                                                                min={0}
                                                                                max={100}
                                                                                placeholder="0"
                                                                                value={entry.weight === 0 && entry.url === '' ? '' : entry.weight}
                                                                                onChange={(e) => updateUrlEntry(index, btnIdx, entryIdx, { weight: Number(e.target.value) || 0 })}
                                                                                className="h-7 w-20 text-xs bg-white"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                ))}

                                                                {entries.length > 0 && !weightOk && (
                                                                    <div className="flex items-center gap-1.5 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-2.5 py-1.5 mt-2">
                                                                        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                                                                        <span>A soma dos pesos deve ser 100%. Atual: <strong>{totalWeight}%</strong></span>
                                                                    </div>
                                                                )}

                                                                <button
                                                                    onClick={() => addUrlEntry(index, btnIdx)}
                                                                    disabled={entries.length >= 5}
                                                                    className="w-full py-1.5 mt-2 text-xs border border-dashed border-blue-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 text-blue-500 hover:text-blue-700 flex items-center justify-center gap-1 transition-colors disabled:opacity-50"
                                                                >
                                                                    <Plus className="w-3.5 h-3.5" />
                                                                    Adicionar URL
                                                                </button>
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Add button to block */}
                            {blockButtons.length < 3 && (
                                <div className="px-3 pb-2 flex gap-1.5">
                                    <button
                                        onClick={() => addButton(index, 'url')}
                                        className="flex-1 py-1.5 text-xs border border-dashed border-gray-300 rounded-md hover:border-blue-400 hover:bg-blue-50 text-gray-500 hover:text-blue-600 flex items-center justify-center gap-1"
                                    >
                                        <Link className="w-3 h-3" />
                                        Botão URL
                                    </button>
                                    <button
                                        onClick={() => addButton(index, 'postback')}
                                        className="flex-1 py-1.5 text-xs border border-dashed border-gray-300 rounded-md hover:border-green-400 hover:bg-green-50 text-gray-500 hover:text-green-600 flex items-center justify-center gap-1"
                                    >
                                        <MousePointerClick className="w-3 h-3" />
                                        Botão Ação
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Add more blocks button */}
            <div className="px-6 pb-3">
                {isAddMenuOpen ? (
                    <div className="grid grid-cols-2 gap-2">
                        {blockTypes.map((bt) => {
                            const Icon = bt.icon;
                            return (
                                <button
                                    key={bt.type}
                                    onClick={() => addBlock(bt.type)}
                                    className="flex items-center gap-2 p-2.5 rounded-lg border border-gray-200 hover:border-purple-400 hover:bg-purple-50 transition-colors"
                                >
                                    <div className={`${bt.color} p-1 rounded`}>
                                        <Icon className="w-3 h-3 text-white" />
                                    </div>
                                    <span className="text-xs text-gray-700">{bt.label}</span>
                                </button>
                            );
                        })}
                        <button
                            onClick={() => setIsAddMenuOpen(false)}
                            className="col-span-2 text-xs text-gray-400 hover:text-gray-600 py-1"
                        >
                            Cancelar
                        </button>
                    </div>
                ) : (
                    <button
                        onClick={() => setIsAddMenuOpen(true)}
                        className="w-full py-2 border border-dashed border-gray-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors flex items-center justify-center gap-1.5 text-gray-400 hover:text-purple-600"
                    >
                        <Plus className="w-3.5 h-3.5" />
                        <span className="text-xs font-medium">Adicionar Bloco</span>
                    </button>
                )}
            </div>

            {/* Save blocks button */}
            {hasChanges && (
                <div className="px-6 pb-4">
                    <Button
                        onClick={() => saveMutation.mutate()}
                        disabled={saveMutation.isPending}
                        size="sm"
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                    >
                        <Save className="w-3.5 h-3.5 mr-1.5" />
                        {saveMutation.isPending ? 'Salvando...' : 'Salvar Blocos'}
                    </Button>
                </div>
            )}

            {/* Media Library Modal */}
            <MediaLibraryModal
                isOpen={mediaModalIndex !== null}
                onClose={() => setMediaModalIndex(null)}
                onSelect={handleMediaSelect}
                pageId={pageId}
                mediaType={mediaModalType}
            />
        </div>
    );
}
